/**
 * Assinatura ICP-Brasil do receituário, no lugar onde a receita já é emitida.
 *
 * Três caminhos na mesma tela, para o médico não precisar aprender um fluxo novo:
 *   cofre  — certificado A1 já guardado; um clique (senha só se ele pediu para não guardar)
 *   token  — certificado A3 no leitor/pendrive, via componente Web PKI
 *   nuvem  — BirdID / VIDaaS (só com REACT_APP_CLOUD_SIGNATURE_ENABLED=true)
 *   novo   — cadastra o A1 agora e já assina, sem sair da página
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudQueue as CloudIcon,
  GppGood as SignIcon,
  Lock as LockIcon,
  PhoneIphone as PhoneIcon,
  Refresh as RefreshIcon,
  UploadFile as UploadIcon,
  Usb as TokenIcon,
  VerifiedUser as CertificateIcon,
  ConfirmationNumber as SncrIcon,
} from '@mui/icons-material';
import {
  CLOUD_API_THEME,
  cloudApiDialogCancelButtonSx,
  cloudApiDialogContentSx,
  cloudApiDialogFooterSx,
  cloudApiDialogHeaderIconSx,
  cloudApiDialogHeaderSx,
  cloudApiDialogPaperSx,
  cloudApiDialogPrimaryButtonSx,
  cloudApiInputFieldSx,
  cloudApiToggleGroupSx,
} from '../../pages/whatsappCloudApiTheme';
import {
  listCertificates,
  listCloudSignatureProviders,
  listTokenCertificates,
  signDocumentWithCloudProvider,
  signPrescriptionWithToken,
  signPrescriptionWithVault,
  signMedicalReportWithToken,
  signMedicalReportWithVault,
  signExamOrderWithVault,
  signExamOrderWithToken,
  signCertificateWithVault,
  signCertificateWithToken,
  signPrescriptionsBatch,
  signPrescriptionsBatchWithToken,
  uploadA1Certificate,
  WebPkiUnavailableError,
  type BatchSignResult,
  type CloudSignatureProvider,
  type CloudSignatureProviderId,
  type ProfessionalCertificate,
  type SignedDocumentInfo,
  type TokenCertificate,
} from '../../services/documentSignatureService';
import { config } from '../../config/environment';
import SncrNumberingDialog from './SncrNumberingDialog';
import SncrAnvisaEmissionStep, { sncrRequestedFromPlan } from './SncrAnvisaEmissionStep';
import SncrGovBrLoginDialog from './SncrGovBrLoginDialog';
import SncrDigitalSignatureRequiredDialog from './SncrDigitalSignatureRequiredDialog';
import {
  getPrescriptionSncrPlan,
  getPrescriptionsSncrPlanBatch,
  reserveSncrBatch,
  SNCR_TIPO_LABELS,
  type SncrAuthRequestStatus,
  type SncrPrescriptionPlan,
  type SncrReceitaTipo,
} from '../../services/sncrService';
import { printSingleLinkedPrescription } from '../../utils/evolutionPrescriptionPrint';

export type IcpSignDocumentKind = 'prescription' | 'medical_report' | 'exam_order' | 'certificate';

const cloudSignatureUiEnabled = config.cloudSignatureEnabled;

interface Props {
  open: boolean;
  kind?: IcpSignDocumentKind;
  document?: any | null;
  /** @deprecated use document */
  prescription?: any | null;
  /**
   * Lote de receituários do mesmo prescritor: uma autorização (senha, código
   * ou aprovação no app) assina todos. Quando presente, ignora `document`.
   */
  documents?: any[] | null;
  onClose: () => void;
  onSigned?: (info: SignedDocumentInfo) => void;
  onBatchSigned?: (result: BatchSignResult) => void;
}

const WEB_PKI_DOWNLOAD_URL = 'https://get.webpkiplugin.com/';

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('pt-BR');
}

function expiresSoon(validUntil?: string | null): boolean {
  if (!validUntil) return false;
  const days = (new Date(validUntil).getTime() - Date.now()) / 86_400_000;
  return days > 0 && days <= 30;
}

function maskCpf(value: string): string {
  return value
    .replace(/\D/g, '')
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
}

const optionCardSx = (selected: boolean) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  p: 1.5,
  borderRadius: 2,
  cursor: 'pointer',
  border: `1px solid ${selected ? CLOUD_API_THEME.teal : CLOUD_API_THEME.borderSoft}`,
  bgcolor: selected ? CLOUD_API_THEME.tealSoft : CLOUD_API_THEME.paper,
  transition: 'border-color 0.15s, background-color 0.15s',
  '&:hover': { borderColor: CLOUD_API_THEME.borderHoverStrong },
});

type Mode = 'vault' | 'token' | 'cloud' | 'new';

const SignPrescriptionDialog: React.FC<Props> = ({
  open,
  kind = 'prescription',
  document,
  prescription,
  documents,
  onClose,
  onSigned,
  onBatchSigned,
}) => {
  const batchDocuments = useMemo(() => (documents || []).filter((d) => d?.id), [documents]);
  const isBatch = batchDocuments.length > 0;
  const batchIds = useMemo(() => batchDocuments.map((d) => String(d.id)), [batchDocuments]);
  const activeDocument = isBatch ? batchDocuments[0] : document || prescription;
  const professionalId = activeDocument?.professional_id || '';
  const docNumber = kind === 'medical_report'
    ? activeDocument?.report_number
    : kind === 'exam_order'
      ? activeDocument?.order_number
      : kind === 'certificate'
        ? activeDocument?.certificate_number || String(activeDocument?.id || '').slice(0, 8).toUpperCase()
        : activeDocument?.prescription_number;
  const docLabel = kind === 'medical_report'
    ? 'Relatório médico'
    : kind === 'exam_order'
      ? 'Pedido de exame'
      : kind === 'certificate'
        ? 'Atestado'
        : 'Receituário';

  const [mode, setMode] = useState<Mode>('vault');
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<string | null>(null);

  const [certificates, setCertificates] = useState<ProfessionalCertificate[]>([]);
  const [selectedCertificateId, setSelectedCertificateId] = useState('');
  const [passphrase, setPassphrase] = useState('');

  const [tokenCertificates, setTokenCertificates] = useState<TokenCertificate[]>([]);
  const [selectedThumbprint, setSelectedThumbprint] = useState('');
  const [tokenLoaded, setTokenLoaded] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [newPassphrase, setNewPassphrase] = useState('');
  const [rememberPassphrase, setRememberPassphrase] = useState(true);

  const [cloudProviders, setCloudProviders] = useState<CloudSignatureProvider[]>([]);
  const [cloudProviderId, setCloudProviderId] = useState<CloudSignatureProviderId>('birdid');
  const [cloudCpf, setCloudCpf] = useState('');
  const [cloudOtp, setCloudOtp] = useState('');

  const [sncrPlan, setSncrPlan] = useState<SncrPrescriptionPlan | null>(null);
  const [sncrDialogOpen, setSncrDialogOpen] = useState(false);
  const [sncrSuggested, setSncrSuggested] = useState<SncrReceitaTipo[]>([]);
  const [sncrLoading, setSncrLoading] = useState(false);
  const [sncrStep, setSncrStep] = useState(false);
  const [sncrError, setSncrError] = useState<string | null>(null);
  const [sncrNotice, setSncrNotice] = useState<string | null>(null);
  const [govBrOpen, setGovBrOpen] = useState(false);
  const [printingPhysical, setPrintingPhysical] = useState(false);
  const [signatureRequiredOpen, setSignatureRequiredOpen] = useState(false);

  const selectedCloudProvider = cloudProviders.find((p) => p.id === cloudProviderId) || null;
  const cloudNeedsOtp = selectedCloudProvider?.challenge === 'otp';

  const vaultCertificates = useMemo(
    () => certificates.filter((certificate) => certificate.certificate_kind === 'a1_file'),
    [certificates],
  );
  const selectedCertificate = vaultCertificates.find((c) => c.id === selectedCertificateId) || null;
  const needsPassphrase = Boolean(selectedCertificate && !selectedCertificate.remember_passphrase);

  const loadVault = useCallback(async () => {
    if (!professionalId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await listCertificates(professionalId);
      setCertificates(list);
      const a1 = list.filter((certificate) => certificate.certificate_kind === 'a1_file');
      setSelectedCertificateId(a1[0]?.id || '');
      // Sem certificado guardado, o caminho útil é cadastrar — já abre nele.
      setMode(a1.length > 0 ? 'vault' : 'new');
    } catch (err: any) {
      setError(err?.message || 'Não foi possível carregar os certificados.');
    } finally {
      setLoading(false);
    }
  }, [professionalId]);

  useEffect(() => {
    if (!open) return;
    setPassphrase('');
    setNewPassphrase('');
    setFile(null);
    setNotice(null);
    setTokenLoaded(false);
    setTokenCertificates([]);
    setSelectedThumbprint('');
    setCloudOtp('');
    void loadVault();
  }, [open, loadVault]);

  const activeDocumentId = activeDocument?.id ? String(activeDocument.id) : '';
  const batchKey = batchIds.join(',');
  const loadSncrPlan = useCallback(async (): Promise<SncrPrescriptionPlan | null> => {
    if (kind !== 'prescription' || (!isBatch && !activeDocumentId)) {
      setSncrPlan(null);
      return null;
    }
    try {
      const plan = isBatch
        ? await getPrescriptionsSncrPlanBatch(batchKey.split(','))
        : await getPrescriptionSncrPlan(activeDocumentId);
      setSncrPlan(plan);
      return plan;
    } catch {
      setSncrPlan(null);
      return null;
    }
  }, [kind, isBatch, batchKey, activeDocumentId]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setSncrStep(false);
    setSncrError(null);
    setSncrNotice(null);
    setGovBrOpen(false);
    setSignatureRequiredOpen(false);
    setSncrLoading(true);
    void loadSncrPlan().then((plan) => {
      if (!active) return;
      setSncrStep(Boolean(plan?.enabled && plan.needs.some((n) => n.missing > 0)));
      setSncrLoading(false);
    });
    return () => { active = false; };
  }, [open, loadSncrPlan]);

  const openSncrDialog = (tipos: SncrReceitaTipo[]) => {
    if (!sncrPlan?.enabled) {
      setSncrSuggested(tipos);
      setSncrDialogOpen(true);
      return;
    }
    setSncrError(null);
    setSncrNotice(null);
    setSncrStep(true);
    void loadSncrPlan();
  };

  const handleGovBrFinished = async (status: SncrAuthRequestStatus) => {
    setGovBrOpen(false);
    if (status.status === 'failed' || status.status === 'expired') {
      setSncrNotice(null);
      setSncrError(
        status.error_message
          || (status.status === 'expired'
            ? 'O login gov.br expirou antes de ser concluído. Tente de novo.'
            : 'A Anvisa não liberou a numeração. Tente de novo.'),
      );
      return;
    }
    const failed = (status.result || []).filter((r) => !r.ok);
    const plan = await loadSncrPlan();
    const stillMissing = Boolean(plan?.needs.some((n) => n.missing > 0));
    if (!stillMissing) {
      setSncrError(null);
      setError(null);
      setSignatureRequiredOpen(true);
      return;
    }
    setSncrNotice(failed.length ? null : 'Numeração recebida da Anvisa.');
    setSncrError(
      failed.length
        ? `Parte da numeração não veio: ${failed.map((r) => `${r.receita_tipo}${r.error ? ` (${r.error})` : ''}`).join(', ')}.`
        : null,
    );
  };

  const handleContinuePhysical = async () => {
    if (!activeDocumentId) return;
    setPrintingPhysical(true);
    setSncrError(null);
    try {
      await printSingleLinkedPrescription(activeDocumentId);
      onClose();
    } catch (err: any) {
      setSncrError(err?.message || 'Não foi possível abrir a impressão do receituário.');
    } finally {
      setPrintingPhysical(false);
    }
  };

  useEffect(() => {
    if (!open || !cloudSignatureUiEnabled) return;
    let active = true;
    void listCloudSignatureProviders()
      .then((list) => {
        if (!active) return;
        setCloudProviders(list);
        const firstEnabled = list.find((p) => p.enabled);
        if (firstEnabled) setCloudProviderId(firstEnabled.id);
      })
      .catch(() => { if (active) setCloudProviders([]); });
    return () => { active = false; };
  }, [open]);

  useEffect(() => {
    if (!cloudSignatureUiEnabled && mode === 'cloud') {
      setMode('vault');
    }
  }, [mode]);

  const loadTokenCertificates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listTokenCertificates();
      setTokenCertificates(list);
      setSelectedThumbprint(list[0]?.thumbprint || '');
      setTokenLoaded(true);
      if (list.length === 0) {
        setError(
          'Nenhum certificado encontrado. Para A3 em nuvem (VIDaaS), abra o VIDaaS Connect, conecte o e-CPF e clique em atualizar. '
          + 'Para token USB, instale o Web PKI e conecte o leitor.',
        );
      }
    } catch (err: any) {
      setError(
        err instanceof WebPkiUnavailableError
          ? err.message
          : err?.message || 'Não foi possível acessar os certificados do computador.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const handleModeChange = (next: Mode | null) => {
    if (!next) return;
    setMode(next);
    setError(null);
    setNotice(null);
    if (next === 'token' && !tokenLoaded) void loadTokenCertificates();
  };

  const finishBatch = (result: BatchSignResult) => {
    if (result.signed.length === 0) {
      const failure = new Error(result.failed[0]?.error || 'Nenhuma receita foi assinada.') as Error & { sncrCode?: string };
      if (result.failed[0]?.code) failure.sncrCode = result.failed[0].code;
      throw failure;
    }
    onBatchSigned?.(result);
  };

  const signWithVault = async (certificateId: string, secret?: string) => {
    if (!activeDocument?.id) return;
    if (isBatch) {
      finishBatch(await signPrescriptionsBatch({
        prescriptionIds: batchIds,
        method: 'vault',
        certificateId,
        passphrase: secret,
      }));
      return;
    }
    const info = kind === 'medical_report'
      ? await signMedicalReportWithVault({
          reportId: activeDocument.id,
          certificateId,
          passphrase: secret,
        })
      : kind === 'exam_order'
        ? await signExamOrderWithVault({
            orderId: activeDocument.id,
            certificateId,
            passphrase: secret,
          })
        : kind === 'certificate'
          ? await signCertificateWithVault({
              certificateId: activeDocument.id,
              vaultCertificateId: certificateId,
              passphrase: secret,
            })
          : await signPrescriptionWithVault({
              prescriptionId: activeDocument.id,
              certificateId,
              passphrase: secret,
            });
    onSigned?.(info);
  };

  const handleSign = async () => {
    if (!activeDocument?.id) return;
    setWorking(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === 'vault') {
        if (!selectedCertificate) throw new Error('Escolha o certificado que vai assinar.');
        if (needsPassphrase && !passphrase) throw new Error('Informe a senha do certificado.');
        await signWithVault(selectedCertificate.id, needsPassphrase ? passphrase : undefined);
        return;
      }

      if (mode === 'cloud' && cloudSignatureUiEnabled) {
        if (!selectedCloudProvider?.enabled) {
          throw new Error('Este provedor não está configurado no servidor. Fale com o suporte.');
        }
        if (cloudCpf.replace(/\D/g, '').length !== 11) {
          throw new Error('Informe o CPF do titular do certificado.');
        }
        if (cloudNeedsOtp && !cloudOtp.trim()) {
          throw new Error(`Informe o código que aparece no app ${selectedCloudProvider.label}.`);
        }
        if (!cloudNeedsOtp) {
          setNotice(
            isBatch
              ? `Enviamos uma única solicitação para o app ${selectedCloudProvider.label}. Aprove no celular para assinar as ${batchIds.length} receitas.`
              : `Enviamos a solicitação para o app ${selectedCloudProvider.label}. Aprove no celular para concluir.`,
          );
        }
        if (isBatch) {
          finishBatch(await signPrescriptionsBatch({
            prescriptionIds: batchIds,
            method: 'cloud',
            provider: selectedCloudProvider.id,
            cpf: cloudCpf,
            otp: cloudNeedsOtp ? cloudOtp.trim() : undefined,
          }));
          return;
        }
        const info = await signDocumentWithCloudProvider({
          kind,
          documentId: activeDocument.id,
          provider: selectedCloudProvider.id,
          cpf: cloudCpf,
          otp: cloudNeedsOtp ? cloudOtp.trim() : undefined,
        });
        onSigned?.(info);
        return;
      }

      if (mode === 'token') {
        if (!selectedThumbprint) throw new Error('Escolha o certificado do token.');
        if (isBatch) {
          // O token assina uma a uma: sem reservar antes, o lote poderia parar no meio.
          if (sncrPlan?.enabled) await reserveSncrBatch(batchIds);
          finishBatch(await signPrescriptionsBatchWithToken({
            prescriptionIds: batchIds,
            thumbprint: selectedThumbprint,
            onProgress: (done, total) => setBatchProgress(done < total ? `Assinando ${done + 1} de ${total}...` : null),
          }));
          return;
        }
        const info = kind === 'medical_report'
          ? await signMedicalReportWithToken({
              reportId: activeDocument.id,
              thumbprint: selectedThumbprint,
            })
          : kind === 'exam_order'
            ? await signExamOrderWithToken({
                orderId: activeDocument.id,
                thumbprint: selectedThumbprint,
              })
            : kind === 'certificate'
              ? await signCertificateWithToken({
                  certificateId: activeDocument.id,
                  thumbprint: selectedThumbprint,
                })
              : await signPrescriptionWithToken({
                  prescriptionId: activeDocument.id,
                  thumbprint: selectedThumbprint,
                });
        onSigned?.(info);
        return;
      }

      if (!file) throw new Error('Selecione o arquivo .pfx ou .p12 do certificado.');
      if (!newPassphrase) throw new Error('Informe a senha do certificado.');
      const stored = await uploadA1Certificate({
        professionalId,
        file,
        passphrase: newPassphrase,
        rememberPassphrase,
      });

      if (stored.warnings.length > 0) {
        // Assinar direto esconderia um alerta que muda a validade do documento.
        setCertificates((current) => [stored.certificate, ...current]);
        setSelectedCertificateId(stored.certificate.id);
        setPassphrase(rememberPassphrase ? '' : newPassphrase);
        setMode('vault');
        setNotice(`${stored.warnings.join(' ')} Confirme para assinar mesmo assim.`);
        return;
      }

      // Guardar e assinar é um único gesto para quem está assinando pela primeira vez.
      await signWithVault(stored.certificate.id, rememberPassphrase ? undefined : newPassphrase);
    } catch (err: any) {
      setError(err?.message || `Não foi possível assinar o ${docLabel.toLowerCase()}.`);
      if (err?.sncrCode === 'SNCR_SALDO_INSUFICIENTE') {
        const missing = Array.isArray(err?.sncrDetails?.missing)
          ? err.sncrDetails.missing.map((m: any) => m.receita_tipo).filter(Boolean)
          : [];
        openSncrDialog(missing);
      }
    } finally {
      setWorking(false);
      setBatchProgress(null);
    }
  };

  const primaryLabel = working
    ? (mode === 'cloud' && !cloudNeedsOtp ? 'Aguardando aprovação...' : batchProgress || 'Assinando...')
    : mode === 'new'
      ? 'Guardar certificado e assinar'
      : isBatch
        ? `Assinar ${batchIds.length} receitas`
        : 'Assinar documento';

  const batchPatientNames = Array.from(
    new Set(batchDocuments.map((d) => String(d.patient_name || '').trim()).filter(Boolean)),
  );

  const showSncrStep = sncrStep && Boolean(sncrPlan);

  return (
    <Dialog
      open={open}
      onClose={() => !working && !printingPhysical && onClose()}
      maxWidth={showSncrStep ? 'md' : 'sm'}
      fullWidth
      PaperProps={{ sx: cloudApiDialogPaperSx }}
    >
      <DialogTitle sx={{ ...cloudApiDialogHeaderSx, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showSncrStep ? <SncrIcon sx={cloudApiDialogHeaderIconSx} /> : <SignIcon sx={cloudApiDialogHeaderIconSx} />}
          {showSncrStep
            ? (isBatch ? `${batchIds.length} receituários` : `${docLabel} ${docNumber || ''}`.trim())
            : 'Assinar com certificado digital'}
        </Box>
        <IconButton onClick={onClose} disabled={working || printingPhysical} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {sncrLoading ? (
        <DialogContent sx={{ ...cloudApiDialogContentSx, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: CLOUD_API_THEME.teal }} />
          </Box>
        </DialogContent>
      ) : showSncrStep && sncrPlan ? (
        <SncrAnvisaEmissionStep
          plan={sncrPlan}
          error={sncrError}
          notice={sncrNotice}
          printingPhysical={printingPhysical}
          onRequestNumbering={() => {
            setSncrError(null);
            setSncrNotice(null);
            setGovBrOpen(true);
          }}
          onContinueDigital={() => {
            setSncrStep(false);
            setError(null);
          }}
          onContinuePhysical={isBatch ? undefined : () => void handleContinuePhysical()}
          onClose={onClose}
        />
      ) : (
      <>
      <DialogContent sx={{ ...cloudApiDialogContentSx, p: 3 }}>
        {isBatch ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: CLOUD_API_THEME.slate, mb: 1 }}>
              {batchIds.length} receituários
              {batchPatientNames.length === 1 ? ` de ${batchPatientNames[0]}` : ''} assinados com uma única
              autorização. A assinatura fica embutida em cada PDF, no padrão ICP-Brasil, e eles não podem mais
              ser alterados depois.
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {batchDocuments.map((d) => (
                <Chip
                  key={d.id}
                  size="small"
                  label={d.prescription_number || String(d.id).slice(0, 8)}
                  sx={{ bgcolor: CLOUD_API_THEME.tealSoft, color: CLOUD_API_THEME.tealDark, fontWeight: 600, fontSize: '0.7rem' }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: CLOUD_API_THEME.slate, mb: 2 }}>
            {docLabel} {docNumber} de {activeDocument?.patient_name}. A assinatura
            fica embutida no PDF, no padrão ICP-Brasil, e o documento não pode mais ser alterado depois.
          </Typography>
        )}

        {sncrPlan?.enabled && sncrPlan.documents.length > 0 && (() => {
          const counts = new Map<SncrReceitaTipo, number>();
          sncrPlan.documents.forEach((d) => counts.set(d.receita_tipo, (counts.get(d.receita_tipo) || 0) + 1));
          const missing = sncrPlan.needs.filter((n) => n.missing > 0);
          return (
            <Alert
              severity={missing.length ? 'warning' : 'info'}
              icon={<SncrIcon fontSize="small" />}
              sx={{ mb: 2, borderRadius: 2 }}
              action={(
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => openSncrDialog(missing.map((n) => n.receita_tipo))}
                  sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  {missing.length ? 'Obter numeração' : 'Ver saldo'}
                </Button>
              )}
            >
              Sai no modelo eletrônico oficial da Anvisa, numerado pelo SNCR:{' '}
              {Array.from(counts.entries()).map(([tipo, n]) => `${n}× ${SNCR_TIPO_LABELS[tipo]}`).join(', ')}.
              {missing.length > 0 && (
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
                  Falta numeração: {missing.map((n) => `${n.receita_tipo} (${n.missing})`).join(', ')}.
                </Box>
              )}
              {sncrPlan.environment === 'hmg' && (
                <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                  Ambiente de homologação: os números não valem em farmácia.
                </Box>
              )}
            </Alert>
          );
        })()}

        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={mode}
          onChange={(_, value) => handleModeChange(value)}
          sx={{ ...cloudApiToggleGroupSx, mb: 2 }}
          disabled={working}
        >
          <ToggleButton value="vault">
            <CertificateIcon fontSize="small" sx={{ mr: 0.75 }} />
            No cofre (A1)
          </ToggleButton>
          <ToggleButton value="token">
            <TokenIcon fontSize="small" sx={{ mr: 0.75 }} />
            Token (A3)
          </ToggleButton>
          {cloudSignatureUiEnabled && (
            <ToggleButton value="cloud">
              <CloudIcon fontSize="small" sx={{ mr: 0.75 }} />
              Nuvem
            </ToggleButton>
          )}
          <ToggleButton value="new">
            <UploadIcon fontSize="small" sx={{ mr: 0.75 }} />
            Cadastrar A1
          </ToggleButton>
        </ToggleButtonGroup>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
            {error.includes('Web PKI') && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  href={WEB_PKI_DOWNLOAD_URL}
                  target="_blank"
                  rel="noopener"
                  sx={{ textTransform: 'none' }}
                >
                  Baixar o componente
                </Button>
              </Box>
            )}
          </Alert>
        )}
        {notice && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {notice}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: CLOUD_API_THEME.teal }} />
          </Box>
        ) : mode === 'vault' ? (
          <Stack spacing={1.25}>
            {vaultCertificates.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Nenhum certificado A1 guardado para este profissional. Use “Cadastrar A1” para enviar o
                arquivo uma única vez.
              </Alert>
            ) : (
              vaultCertificates.map((certificate) => (
                <Box
                  key={certificate.id}
                  onClick={() => setSelectedCertificateId(certificate.id)}
                  sx={optionCardSx(certificate.id === selectedCertificateId)}
                >
                  <Radio size="small" checked={certificate.id === selectedCertificateId} sx={{ p: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: CLOUD_API_THEME.navy }}>
                      {certificate.subject_common_name || certificate.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate, display: 'block' }}>
                      {certificate.issuer_common_name || 'Emissor não identificado'} · válido até{' '}
                      {formatDate(certificate.valid_until)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                      {certificate.remember_passphrase && (
                        <Chip
                          label="Assinatura em 1 clique"
                          size="small"
                          sx={{ bgcolor: CLOUD_API_THEME.tealSoft, color: CLOUD_API_THEME.tealDark, fontWeight: 600, fontSize: '0.65rem' }}
                        />
                      )}
                      {!certificate.is_icp_brasil && (
                        <Chip label="Fora da ICP-Brasil" size="small" sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 600, fontSize: '0.65rem' }} />
                      )}
                      {expiresSoon(certificate.valid_until) && (
                        <Chip label="Vence em breve" size="small" sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 600, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </Box>
                </Box>
              ))
            )}

            {needsPassphrase && (
              <TextField
                fullWidth
                size="small"
                type="password"
                label="Senha do certificado"
                value={passphrase}
                onChange={(event) => setPassphrase(event.target.value)}
                disabled={working}
                autoFocus
                sx={cloudApiInputFieldSx}
                InputProps={{ startAdornment: <LockIcon fontSize="small" sx={{ mr: 1, color: CLOUD_API_THEME.slateFaint }} /> }}
                helperText="A senha é usada só nesta assinatura e não fica guardada."
              />
            )}
          </Stack>
        ) : mode === 'cloud' && cloudSignatureUiEnabled ? (
          <Stack spacing={1.5}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Certificado em nuvem: a chave fica no provedor e nada precisa ser instalado neste
              computador. Você autoriza a assinatura pelo app no celular.
            </Alert>

            {cloudProviders.length === 0 ? (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                Nenhum provedor de assinatura em nuvem está configurado neste servidor.
              </Alert>
            ) : (
              cloudProviders.map((provider) => (
                <Box
                  key={provider.id}
                  onClick={() => {
                    if (!provider.enabled) return;
                    setCloudProviderId(provider.id);
                    setError(null);
                    setNotice(null);
                  }}
                  sx={{
                    ...optionCardSx(provider.id === cloudProviderId && provider.enabled),
                    opacity: provider.enabled ? 1 : 0.55,
                    cursor: provider.enabled ? 'pointer' : 'not-allowed',
                  }}
                >
                  <Radio
                    size="small"
                    checked={provider.id === cloudProviderId && provider.enabled}
                    disabled={!provider.enabled}
                    sx={{ p: 0.25 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: CLOUD_API_THEME.navy }}>
                      {provider.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate, display: 'block' }}>
                      {provider.enabled
                        ? provider.instructions
                        : 'Não configurado neste servidor — fale com o suporte.'}
                    </Typography>
                  </Box>
                  <Chip
                    icon={provider.challenge === 'push' ? <PhoneIcon sx={{ fontSize: '0.85rem !important' }} /> : undefined}
                    label={provider.challenge === 'push' ? 'Aprovação no app' : 'Código do app'}
                    size="small"
                    sx={{ bgcolor: CLOUD_API_THEME.tealSoft, color: CLOUD_API_THEME.tealDark, fontWeight: 600, fontSize: '0.65rem' }}
                  />
                </Box>
              ))
            )}

            <TextField
              fullWidth
              size="small"
              label="CPF do titular do certificado"
              placeholder="Digite aqui o número do seu CPF"
              value={cloudCpf}
              onChange={(event) => setCloudCpf(maskCpf(event.target.value))}
              disabled={working || !selectedCloudProvider?.enabled}
              sx={cloudApiInputFieldSx}
              inputProps={{ inputMode: 'numeric' }}
            />

            {cloudNeedsOtp && (
              <TextField
                fullWidth
                size="small"
                label={`Código do app ${selectedCloudProvider?.label || ''}`}
                value={cloudOtp}
                onChange={(event) => setCloudOtp(event.target.value.replace(/\D/g, '').slice(0, 8))}
                disabled={working || !selectedCloudProvider?.enabled}
                sx={cloudApiInputFieldSx}
                inputProps={{ inputMode: 'numeric' }}
                helperText="O código muda a cada poucos segundos: gere e confirme na sequência."
              />
            )}

            {working && !cloudNeedsOtp && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <CircularProgress size={18} sx={{ color: CLOUD_API_THEME.teal }} />
                <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate }}>
                  Aguardando sua aprovação no app {selectedCloudProvider?.label}. Não feche esta janela.
                </Typography>
              </Box>
            )}
          </Stack>
        ) : mode === 'token' ? (
          <Stack spacing={1.25}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <strong>Token físico:</strong> conecte o leitor e instale o{' '}
              <a href={WEB_PKI_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">Web PKI</a>.
              <br />
              <strong>A3 em nuvem (VIDaaS, SafeID…):</strong> abra o app do provedor neste Mac/PC, faça login e deixe o
              certificado conectado; depois clique em atualizar abaixo. Use preferencialmente o Google Chrome.
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate }}>
                Certificados encontrados neste computador
              </Typography>
              <Tooltip title="Procurar de novo">
                <span>
                  <IconButton size="small" onClick={() => void loadTokenCertificates()} disabled={working}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {tokenCertificates.map((certificate) => (
              <Box
                key={certificate.thumbprint}
                onClick={() => setSelectedThumbprint(certificate.thumbprint)}
                sx={optionCardSx(certificate.thumbprint === selectedThumbprint)}
              >
                <Radio size="small" checked={certificate.thumbprint === selectedThumbprint} sx={{ p: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: CLOUD_API_THEME.navy }}>
                    {certificate.subjectName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate, display: 'block' }}>
                    {certificate.issuerName} · válido até {formatDate(certificate.validityEnd)}
                  </Typography>
                </Box>
              </Box>
            ))}

            {tokenLoaded && tokenCertificates.length > 0 && (
              <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate }}>
                Ao confirmar, o token pede o PIN. A chave privada não sai do dispositivo.
              </Typography>
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              O arquivo é guardado cifrado no servidor e só é aberto no momento de assinar. Envie uma vez
              e as próximas receitas saem assinadas em um clique.
            </Alert>

            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadIcon />}
              disabled={working}
              sx={{ textTransform: 'none', borderRadius: 2, justifyContent: 'flex-start', py: 1.25 }}
            >
              {file ? file.name : 'Escolher arquivo .pfx ou .p12'}
              <input
                hidden
                type="file"
                accept=".pfx,.p12"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
              />
            </Button>

            <TextField
              fullWidth
              size="small"
              type="password"
              label="Senha do certificado"
              value={newPassphrase}
              onChange={(event) => setNewPassphrase(event.target.value)}
              disabled={working}
              sx={cloudApiInputFieldSx}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={rememberPassphrase}
                  onChange={(event) => setRememberPassphrase(event.target.checked)}
                  disabled={working}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Assinar em 1 clique</Typography>
                  <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate }}>
                    Guarda a senha cifrada. Desligue se preferir digitar a senha em cada assinatura.
                  </Typography>
                </Box>
              }
            />
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slateFaint }}>
          O certificado precisa ser do próprio prescritor: assinatura de outra pessoa invalida o documento.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ ...cloudApiDialogFooterSx, p: 3, gap: 1 }}>
        <Button onClick={onClose} disabled={working} sx={cloudApiDialogCancelButtonSx}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSign()}
          disabled={working || loading}
          startIcon={working ? <CircularProgress size={18} color="inherit" /> : <SignIcon />}
          sx={cloudApiDialogPrimaryButtonSx}
        >
          {primaryLabel}
        </Button>
      </DialogActions>
      </>
      )}

      <SncrDigitalSignatureRequiredDialog
        open={signatureRequiredOpen}
        tipos={(sncrPlan?.documents || []).map((d) => d.receita_tipo)}
        onCancel={() => {
          setSignatureRequiredOpen(false);
          onClose();
        }}
        onSign={() => {
          setSignatureRequiredOpen(false);
          setNotice('Numeração SNCR recebida. Escolha o certificado para assinar.');
          setSncrStep(false);
        }}
      />

      {professionalId && sncrPlan && (
        <SncrGovBrLoginDialog
          open={govBrOpen}
          professionalId={professionalId}
          requested={sncrRequestedFromPlan(sncrPlan)}
          onClose={() => setGovBrOpen(false)}
          onFinished={(status) => void handleGovBrFinished(status)}
        />
      )}

      {professionalId && (
        <SncrNumberingDialog
          open={sncrDialogOpen}
          professionalId={professionalId}
          suggestedTipos={sncrSuggested}
          onClose={() => setSncrDialogOpen(false)}
          onCompleted={() => {
            setError(null);
            setNotice('Numeração SNCR recebida. Confirme a assinatura.');
            setSncrDialogOpen(false);
            void loadSncrPlan();
          }}
        />
      )}
    </Dialog>
  );
};

export default SignPrescriptionDialog;
